import { useState, useEffect } from 'react';
import { IntroMotion } from './components/IntroMotion';
import { Navbar } from './components/Navbar';
import { HeroSection } from './components/HeroSection';
import { SolarGrowthSection } from './components/SolarGrowthSection';
import { PainPointsSection } from './components/PainPointsSection';
import { BenefitsSection } from './components/BenefitsSection';
import { EligibilitySection } from './components/EligibilitySection';
import { ConsultationForm } from './components/ConsultationForm';
import { Footer } from './components/Footer';
import { MobileBottomBar } from './components/MobileBottomBar';
import { GridCalculatorPage } from './components/GridCalculatorPage';
import { AdminAuthPage } from './components/AdminAuthPage';
import { AdminPortalPage } from './components/AdminPortalPage';
import { AdminProposalPage, type SharedProposalData } from './components/AdminProposalPage';
import { AdminGridDiagnosticPage } from './components/AdminGridDiagnosticPage';
import { AdminGoldenTargetPage } from './components/AdminGoldenTargetPage';
import { AdminGridMapPage } from './components/AdminGridMapPage';

export type AppPage =
  | 'landing'
  | 'calculator'
  | 'admin-portal'
  | 'admin-proposal'
  | 'admin-diagnostic'
  | 'admin-golden-targets'
  | 'admin-map';

const ADMIN_PAGES: AppPage[] = [
  'admin-portal',
  'admin-proposal',
  'admin-diagnostic',
  'admin-golden-targets',
  'admin-map'
];

export function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [currentPage, setCurrentPage] = useState<AppPage>('landing');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('plus_sd_admin_auth') === 'true';
  });
  const [pendingAdminPage, setPendingAdminPage] = useState<AppPage>('admin-portal');

  // Shared state between Diagnostic / Golden Target / Proposal
  const [sharedProposalData, setSharedProposalData] = useState<SharedProposalData>({
    address: '',
    clientName: '',
    clientPhone: '',
    capacityKw: 250,
  });

  // Browser History & Back/Forward Button Synchronization
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (e.state && e.state.page) {
        setCurrentPage(e.state.page);
      } else {
        const hash = window.location.hash.replace('#', '') as AppPage;
        if (ADMIN_PAGES.includes(hash) || hash === 'calculator') {
          setCurrentPage(hash);
        } else {
          setCurrentPage('landing');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Initial state registration
    if (!window.history.state) {
      window.history.replaceState({ page: 'landing' }, '', window.location.pathname);
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const scrollToForm = () => {
    const elem = document.getElementById('consultation');
    if (elem) elem.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavigatePage = (page: AppPage) => {
    if (ADMIN_PAGES.includes(page)) {
      setPendingAdminPage(page);
    }
    
    try {
      window.history.pushState({ page }, '', page === 'landing' ? window.location.pathname : `#${page}`);
    } catch {
      // Ignore if pushState blocked
    }

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Dynamic Noindex for Admin security vs Index for landing
  useEffect(() => {
    const metaRobots = document.getElementById('robots-meta');
    if (metaRobots) {
      if (ADMIN_PAGES.includes(currentPage)) {
        metaRobots.setAttribute('content', 'noindex, nofollow');
      } else {
        metaRobots.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
      }
    }
  }, [currentPage]);

  const handleAuthSuccess = () => {
    setIsAdminAuthenticated(true);
    handleNavigatePage(pendingAdminPage || 'admin-portal');
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem('plus_sd_admin_auth');
    } catch {
      // ignore
    }
    setIsAdminAuthenticated(false);
    handleNavigatePage('landing');
  };

  const handlePassToProposal = (data: SharedProposalData) => {
    setSharedProposalData(data);
    handleNavigatePage('admin-proposal');
  };

  // Calculator Page
  if (currentPage === 'calculator') {
    return (
      <GridCalculatorPage onBackToHome={() => handleNavigatePage('landing')} />
    );
  }

  // Admin Pages Security Gate
  if (ADMIN_PAGES.includes(currentPage) && !isAdminAuthenticated) {
    return (
      <AdminAuthPage
        onSuccess={handleAuthSuccess}
        onBackToHome={() => handleNavigatePage('landing')}
      />
    );
  }

  // Admin Subpages (Authorized)
  if (currentPage === 'admin-portal') {
    return (
      <AdminPortalPage
        onBackToHome={() => handleNavigatePage('landing')}
        onLogout={handleLogout}
        onNavigateSubpage={(subpage) => handleNavigatePage(subpage)}
      />
    );
  }

  if (currentPage === 'admin-proposal') {
    return (
      <AdminProposalPage
        initialData={sharedProposalData}
        onBackToHome={() => handleNavigatePage('landing')}
        onBackToPortal={() => handleNavigatePage('admin-portal')}
        onNavigateSubpage={(subpage) => handleNavigatePage(subpage)}
      />
    );
  }

  if (currentPage === 'admin-diagnostic') {
    return (
      <AdminGridDiagnosticPage
        onBackToHome={() => handleNavigatePage('landing')}
        onBackToPortal={() => handleNavigatePage('admin-portal')}
        onNavigateSubpage={(subpage) => handleNavigatePage(subpage)}
        onPassToProposal={handlePassToProposal}
      />
    );
  }

  if (currentPage === 'admin-golden-targets') {
    return (
      <AdminGoldenTargetPage
        onBackToHome={() => handleNavigatePage('landing')}
        onBackToPortal={() => handleNavigatePage('admin-portal')}
        onNavigateSubpage={(subpage) => handleNavigatePage(subpage)}
      />
    );
  }

  if (currentPage === 'admin-map') {
    return (
      <AdminGridMapPage
        onBackToHome={() => handleNavigatePage('landing')}
        onBackToPortal={() => handleNavigatePage('admin-portal')}
        onNavigateSubpage={(subpage) => handleNavigatePage(subpage)}
      />
    );
  }

  // Main Landing Page
  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-sky-500 selection:text-white">
      {/* 0. Intro Opening Motion */}
      {showIntro && (
        <IntroMotion onComplete={() => setShowIntro(false)} />
      )}

      {/* Header Navigation */}
      <Navbar
        currentPage={currentPage}
        onNavigatePage={handleNavigatePage}
      />

      <main>
        {/* Section 1: 메인 히어로 배너 */}
        <HeroSection
          onOpenConsultation={scrollToForm}
          onNavigateCalculator={() => handleNavigatePage('calculator')}
        />

        {/* Section 2: 다들 누리고 있는 태양광 '로또지붕' & 연도별 발전량 성장 그래프 */}
        <SolarGrowthSection />

        {/* Section 3: 문제 제기 및 공감 (3가지 사장님 고민 & 솔루션) */}
        <PainPointsSection />

        {/* Section 4: 플러스에스디 3대 혜택 (수리비 지원 / 1등급 시공 / 정산 시스템) */}
        <BenefitsSection />

        {/* Section 5: 설치 조건 및 자격 안내 (부지, 공간, 수도권/강원도, 4단계 절차) */}
        <EligibilitySection />

        {/* Section 6: 실시간 예상 수익 계산기 & 간편 견적 문의 (010-2767-1449) */}
        <ConsultationForm />
      </main>

      {/* Footer & Mobile Sticky Bar */}
      <Footer onNavigateAdminPortal={() => handleNavigatePage('admin-portal')} />
      <MobileBottomBar
        onOpenConsultation={scrollToForm}
        onNavigateCalculator={() => handleNavigatePage('calculator')}
      />
    </div>
  );
}

export default App;
